import { inject, observer } from "mobx-react";
import { Component } from "react";
import { injectIntl, type IntlShape } from "react-intl";
import { compose } from "nvent-web/utils/compose";

import { LoadingSection } from "nvent-web/components/LoadingSection/LoadingSection";
import ProjectForm from "nvent-web/components/ProjectForm";
import { ProjectsStore } from "nvent-web/stores/Projects";
import { type DetailedProject } from "nvent-web/types/DetailedProject";
import { type NewProjectFormValues } from "nvent-web/types/NewProjectFormValues";
import { withRouter, type WithRouterProps } from "~/nvent-web/utils/withRouter";

interface EditProjectParams {
  projectId: string;
}

interface EditProjectFormInnerProps extends EditProjectParams, WithRouterProps {
  intl: IntlShape;
  projects: ProjectsStore;
}

class EditProjectFormInner extends Component<EditProjectFormInnerProps> {
  initialValues(project: DetailedProject): NewProjectFormValues {
    return {
      project: {
        name: project.name,
        addressAttributes: {
          streetAddress: project.address.streetAddress,
          postalCode: project.address.postalCode,
          locality: project.address.locality,
        },
        clientName: project.clientName,
        clientCompany: project.clientCompany,
        clientEmail: project.clientEmail,
        clientPhoneNumber: project.clientPhoneNumber,
      },
    };
  }

  componentDidMount() {
    this.props.projects.getProject(Number(this.props.params.projectId));
  }

  renderForm(values: DetailedProject) {
    const { projects, intl } = this.props;

    return (
      <ProjectForm
        initialValues={this.initialValues(values)}
        onCancel={this.handleCancel}
        handleSubmit={this.handleSubmit}
        isSubmitting={projects.isProjectLoading}
        submitLabel={intl.formatMessage({ id: "actions.update" })}
      />
    );
  }

  render() {
    const { projectDetails, areProjectDetailsLoading } = this.props.projects;

    if (areProjectDetailsLoading) {
      return <LoadingSection />;
    }

    return <>{projectDetails && this.renderForm(projectDetails)}</>;
  }

  private handleSubmit = async (formValues: NewProjectFormValues) => {
    const { projects } = this.props;
    if (projects.projectDetails) {
      await projects.updateProject(formValues, projects.projectDetails.id);
      this.props.navigate(`/projects/${projects.projectDetails.id}`);
    }
  };

  private handleCancel = () => {
    this.props.navigate(-1);
  };
}

const EditProjectForm = compose(
  inject("projects"),
  injectIntl,
  withRouter,
  observer
)(EditProjectFormInner);

export default EditProjectForm;
